import { createContext, use } from 'react';

import type { GeolocationPermission, LocationPreference } from '../../lib/device-location';
import type { AccessLogLocation } from '../../types';

/** Where the last attempt to share ended. */
export type DeviceLocationStatus =
  | 'idle'
  | 'requesting'
  | 'shared'
  | 'denied'
  | 'unavailable'
  | 'timeout'
  | 'unsupported'
  | 'imprecise'
  | 'unresolved'
  | 'failed';

export interface DeviceLocationValue {
  readonly supported: boolean;
  readonly permission: GeolocationPermission;
  /** This user's choice on this browser; null until they make one. */
  readonly preference: LocationPreference | null;
  readonly status: DeviceLocationStatus;
  /** The place the API resolved the last shared position to. */
  readonly place: AccessLogLocation | null;
  /** "Share location": may show the browser prompt. Call only from a click. */
  readonly enable: () => Promise<void>;
  /** "Stop sharing": forgets the session's location on the server. */
  readonly disable: () => Promise<void>;
  /** "Not now": hides the banner for good on this browser. */
  readonly dismiss: () => void;
  /**
   * Before opening a project: refreshes a stale location, only when sharing is
   * on and the browser already allows it, so it never prompts. Waits at most a
   * moment and never fails the open.
   */
  readonly refreshBeforeOpen: () => Promise<void>;
}

const noop = (): Promise<void> => Promise.resolve();

/** Outside the provider (e.g. a component rendered alone in a test) nothing is shared. */
export const DeviceLocationContext = createContext<DeviceLocationValue>({
  supported: false,
  permission: 'unknown',
  preference: null,
  status: 'idle',
  place: null,
  enable: noop,
  disable: noop,
  dismiss: () => undefined,
  refreshBeforeOpen: noop,
});

export function useDeviceLocation(): DeviceLocationValue {
  return use(DeviceLocationContext);
}
